import React from 'react';
import { withTranslation } from 'react-i18next';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import * as Sentry from '@sentry/react';
import { safeSessionStorage } from '../utils/safeStorage';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    // Forward to Sentry so production crashes are visible. Without this the
    // top-level boundary swallows everything and we never learn about issues.
    try {
      Sentry.captureException(error, {
        contexts: { react: { componentStack: errorInfo?.componentStack } },
      });
    } catch (_) { /* Sentry init failures shouldn't break the boundary itself */ }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    // Clear any potentially corrupted session data
    safeSessionStorage.removeItem('bride_form_draft');
    safeSessionStorage.removeItem('groom_form_draft');
    window.location.reload();
  };

  render() {
    const { t } = this.props;

    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              {t('errorBoundary.title')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('errorBoundary.message')}
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-left" dir="ltr">
                <p className="text-red-700 text-sm font-mono break-all">
                  {this.state.error.toString()}
                </p>
              </div>
            )}
            <Button
              onClick={this.handleReset}
              className="bg-wefaqna-forest text-white hover:bg-wefaqna-forest-hover"
            >
              <RefreshCw className="h-4 w-4 me-2" />
              {t('errorBoundary.reloadBtn')}
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default withTranslation()(ErrorBoundary);
